// La page d'un événement : quand, où, quoi, et qui vient.
//
// Deux réponses seulement — « Je viens » et « Peut-être ». Pas de « Non » :
// dire qu'on ne vient pas n'apprend rien à personne, et le silence le dit déjà.
// Facebook l'a compris tard ; autant commencer juste.
//
// L'organisateur voit deux boutons de plus : modifier et supprimer. Un
// modérateur aussi — un événement mal annoncé fait venir des gens pour rien.

import { useEffect, useState } from "react";
import Store from "../../store.js";
import { toast, confirmer } from "../../ui.js";
import { estModerateur } from "../../views/moderation-outils.js";
import { editerEvenement } from "../../views/evenement-creer.js";
import { quandLisible, estPasse, enDirect, lecteurIntegrable } from "../dates.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { useAbonnement, useMembre } from "../hooks.js";

/**
 * Le direct.
 *
 * Koinonia ne DIFFUSE pas : cela demanderait un serveur d'ingestion et de
 * transcodage, c'est-à-dire une infrastructure payante. Elle montre ici un
 * direct qui existe déjà sur YouTube ou Facebook — ce que font les églises de
 * toute façon. La politique de sécurité de l'application n'autorise que ces
 * deux lecteurs, et rien d'autre.
 */
function Direct({ e }) {
  const lecteur = lecteurIntegrable(e.lienDirect);
  return (
    <section className="direct">
      <div className="direct__titre">
        <span className="direct-pastille" /> EN DIRECT
      </div>
      {lecteur ? (
        <div className="direct__cadre">
          <iframe
            src={lecteur}
            title={`Direct — ${e.titre}`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        // Lien non reconnu : plutôt que d'afficher un cadre vide, on propose de
        // l'ouvrir. Mieux vaut un bouton honnête qu'un lecteur qui ne charge pas.
        <a className="btn btn--primaire btn--bloc" href={e.lienDirect} target="_blank" rel="noopener noreferrer">
          Ouvrir le direct
        </a>
      )}
    </section>
  );
}

const REPONSES = [
  { id: "oui", label: "Je viens" },
  { id: "peutetre", label: "Peut-être" },
];

function Participants({ gens }) {
  if (!gens.length)
    return <p className="texte-doux petit">Personne n'a encore répondu. Soyez le premier 🙂</p>;

  const viennent = gens.filter((g) => g.reponse === "oui");
  const peutetre = gens.filter((g) => g.reponse === "peutetre");

  const bloc = (liste, titre) =>
    liste.length > 0 && (
      <>
        <h3 className="sous-titre">
          {titre} ({liste.length})
        </h3>
        <div className="grille-membres">
          {liste.map((u) => (
            <a className="membre" href={`#/profil/${u.uid}`} key={u.uid}>
              <span className="membre__qui">
                <Avatar membre={u} taille={44} />
                <span>
                  <strong>
                    {u.nom}
                    <Certifie membre={u} taille={13} />
                  </strong>
                  <span className="petit texte-doux">{u.eglise || u.ville || "Membre"}</span>
                </span>
              </span>
            </a>
          ))}
        </div>
      </>
    );

  return (
    <>
      {bloc(viennent, "Viennent")}
      {bloc(peutetre, "Peut-être")}
    </>
  );
}

export default function Evenement({ ctx, params }) {
  const id = params.id;
  const [etat, setEtat] = useState({ phase: "chargement" });
  const participants = useAbonnement((cb) => Store.evenements.subscribeParticipants(id, cb), [], [id]);
  const organisateur = useMembre(ctx, etat.e?.creeParId, {
    nom: etat.e?.auteurNom,
    photo: etat.e?.auteurPhoto,
  });

  const charger = () => {
    let vivant = true;
    Store.evenements
      .get(id)
      .then((e) => vivant && setEtat(e ? { phase: "prete", e } : { phase: "introuvable" }))
      .catch(() => vivant && setEtat({ phase: "introuvable" }));
    return () => {
      vivant = false;
    };
  };

  useEffect(() => {
    setEtat({ phase: "chargement" });
    return charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (etat.phase === "chargement")
    return (
      <div className="colonne">
        <Chargement texte="Chargement de l'événement…" />
      </div>
    );

  if (etat.phase === "introuvable")
    return (
      <div className="colonne">
        <Vide symbole="🗓️" titre="Événement introuvable" texte="Il a peut-être été annulé." />
      </div>
    );

  const e = etat.e;
  const passe = estPasse(e.quand);
  const maReponse = participants.find((p) => p.uid === ctx.moi.uid)?.reponse || null;
  const jeSuisOrganisateur = e.creeParId === ctx.moi.uid;

  const repondre = async (r) => {
    try {
      // toucher deux fois la même réponse, c'est se retirer
      await Store.evenements.repondre(id, maReponse === r ? null : r);
    } catch (err) {
      toast(err.message || "Réponse impossible", "erreur");
    }
  };

  const supprimer = async () => {
    if (
      await confirmer(
        "Supprimer cet événement ?",
        "Il disparaîtra pour tous les membres, avec la liste des participants.",
        "Supprimer"
      )
    ) {
      await Store.evenements.supprimer(id);
      toast("Événement supprimé");
      location.hash = "#/evenements";
    }
  };

  return (
    <div className="colonne">
      {/* Le direct d'abord : quelqu'un qui ouvre cette page pendant le culte
          vient pour le regarder, pas pour lire la description. */}
      {enDirect(e) && <Direct e={e} />}

      <section className="groupe-tete">
        {e.image ? (
          <div className="profil__couverture" style={{ backgroundImage: `url('${e.image}')` }} />
        ) : (
          <div className="groupe-tete__banniere">🗓️</div>
        )}
        <div className="groupe-tete__corps">
          <h1>{e.titre}</h1>

          <ul className="details">
            <li>
              <Icone nom="alertes" taille={19} />{" "}
              <strong className={passe ? "texte-doux" : undefined}>
                {quandLisible(e.quand)}
                {passe && " — terminé"}
              </strong>
            </li>
            {e.lieu && (
              <li>
                <Icone nom="fil" taille={19} /> {e.lieu}
              </li>
            )}
            <li>
              <Icone nom="profil" taille={19} /> Organisé par{" "}
              <a href={`#/profil/${e.creeParId}`}>
                <strong>{organisateur.nom}</strong>
              </a>
              <Certifie membre={organisateur} taille={13} />
            </li>
          </ul>

          {e.description && (
            <p className="groupe-tete__desc">
              <TexteRiche texte={e.description} />
            </p>
          )}

          {/* Passé, on ne demande plus si l'on vient : la question n'a plus de sens. */}
          {!passe && (
            <div className="profil__actions">
              {REPONSES.map((r) => (
                <button
                  key={r.id}
                  className={`btn ${maReponse === r.id ? "btn--primaire" : "btn--gris"}`}
                  onClick={() => repondre(r.id)}
                >
                  {maReponse === r.id && "✓ "}
                  {r.label}
                </button>
              ))}
            </div>
          )}

          {(jeSuisOrganisateur || estModerateur(ctx.moi)) && (
            <div className="profil__actions">
              <button
                className="btn btn--gris"
                onClick={() => editerEvenement(ctx, e, {}, () => charger())}
              >
                <Icone nom="appareil" taille={17} /> Modifier
              </button>
              <button className="btn btn--gris btn--carre" aria-label="Supprimer" onClick={supprimer}>
                <Icone nom="fermer" taille={18} />
              </button>
            </div>
          )}
        </div>
      </section>

      <h2 className="titre-section">Qui vient</h2>
      <Participants gens={participants} />
    </div>
  );
}
